import { DatesRangeValue } from '@mantine/dates';
import { hideNotification, showNotification } from '@mantine/notifications';
import { Loader } from '@mantine/core';
import dayjs from 'dayjs';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import { faqsListService, faqsImportStatusService, FaqSourceType } from '@mygpt/app/faqs/services/list';
import { faqsSearchListFromDatasetsService } from '@mygpt/app/faqs/services/search';
import { faqsStore } from '@mygpt/app/faqs/store';
import { useTranslation } from 'react-i18next';

export const useFAQsList = (datasetId: string, options: { defaultPageSize?: number } = {}) => {
  const { i18n, t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const { defaultPageSize = 10 } = options;
  const store = useStore(faqsStore);
  const data = useMemo(() => store.records[datasetId] ?? [], [store.records, datasetId]);
  const [page, setPage] = React.useState<{ page: number; total: number; size: number; pages: number }>({
    page: 1,
    total: data.length,
    pages: 1,
    size: defaultPageSize,
  });
  const totalRef = useRef<number>(0);
  const pollingTimeoutRef = useRef<NodeJS.Timeout>();

  const [faqTypeValue, setFaqTypeValue] = React.useState<FaqSourceType | ''>('');
  const [rangeDate, setRangeDate] = React.useState<string[]>([]);
  const handleDateChange = (date: DatesRangeValue | null, text: string) => {
    if (!rangeDate.length && (!date || !date.length)) return;
    let dateTime: string[] = [];
    if (date) {
      dateTime = date.map((item) => (item && dayjs(item).format('YYYY-MM-DD HH:mm:ss')) || '');
    }
    setRangeDate(dateTime);
  };

  const [sortBy, setSortBy] = React.useState<string>('');
  const [reverseSortDirection, setReverseSortDirection] = React.useState(false);
  const setSorting = (field: string) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  useLayoutEffect(() => {
    setPage((prev) => {
      const total = (store.cacheTotal[datasetId] ?? 0) + totalRef.current;
      return {
        ...prev,
        total,
        pages: Math.ceil(total / prev.size),
      };
    });
  }, [datasetId, store.cacheTotal]);

  const checkImportStatus = React.useCallback(
    async (onImportCompleted?: () => void) => {
      try {
        const currentLanguage = i18n.language; // 总是使用最新的语言
        const statusRes = await faqsImportStatusService({ datasetId, language: currentLanguage });
        if (statusRes.data.status === 'importing') {
          const { import_progress } = statusRes.data;
          const { success_faqs, failed_faqs, total_faqs } = import_progress;

          showNotification({
            id: 'importing-faq-list',
            message: t('create.faqs.translating', {
              success_count: `${success_faqs}/${total_faqs}`,
              failed_count: `${failed_faqs}/${total_faqs}`,
            }),
            color: 'white',
            icon: React.createElement(Loader, { size: 20 }),
            autoClose: false,
            withCloseButton: false,
          });

          // 清理现有定时器
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }

          // Poll import status again after 5 seconds
          pollingTimeoutRef.current = setTimeout(() => {
            checkImportStatus(onImportCompleted); // 不再传递语言参数
          }, 5000);
          return true; // 正在导入
        } else {
          // Hide import notification if it exists
          hideNotification('importing-faq-list');

          // 如果之前在导入，现在完成了，刷新FAQ列表
          if (pollingTimeoutRef.current && onImportCompleted) {
            onImportCompleted();
          }

          return false; // 没有导入
        }
      } catch (error) {
        // 如果导入状态接口出错，假设没有导入
        hideNotification('importing-faq-list');
        return false;
      }
    },
    [datasetId, t] // 移除 i18n.language，因为函数内部直接获取最新语言
  );

  const loadList = React.useCallback(
    async (params: Omit<Parameters<typeof faqsListService>[0], 'datasetId'>) => {
      setLoading(true);

      // 定义导入完成时的回调函数，重新获取FAQ数据
      const onImportCompleted = () => {
        // 使用最新的语言参数，避免闭包中的旧语言
        const currentParams = {
          ...params,
          language: i18n.language,
        };
        faqsListService({ datasetId, size: defaultPageSize, ...currentParams })
          .then((res) => {
            faqsStore.setState({
              records: {
                [datasetId]: res.data.items,
              },
              cacheTotal: {
                [datasetId]: 0,
              },
            });
            setPage({
              page: res.data.page,
              total: res.data.total,
              pages: res.data.pages,
              size: res.data.size,
            });
            totalRef.current = res.data.total;
          })
          .catch((error) => {
            console.error('Failed to refresh FAQ list after import completion:', error);
          });
      };

      // 首先检查导入状态
      const isImporting = await checkImportStatus(onImportCompleted);

      // 不论是否在导入，都获取FAQ数据
      return faqsListService({ datasetId, size: defaultPageSize, ...params })
        .then((res) => {
          // Handle normal FAQ list response
          faqsStore.setState({
            records: {
              [datasetId]: res.data.items,
            },
            cacheTotal: {
              [datasetId]: 0,
            },
          });
          setPage({
            page: res.data.page,
            total: res.data.total,
            pages: res.data.pages,
            size: res.data.size,
          });
          totalRef.current = res.data.total;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId, defaultPageSize] // 移除 checkImportStatus 依赖，避免重复创建
  );

  const abortControllerRef = useRef<AbortController>();

  const searchList = React.useCallback(
    async (params: Omit<Parameters<typeof faqsSearchListFromDatasetsService>[0], 'datasetId'>) => {
      if (!datasetId) return Promise.resolve();
      setLoading(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      return faqsSearchListFromDatasetsService({ datasetId, ...params })
        .then((res) => {
          if (!!res.data && typeof res.data === 'object') {
            faqsStore.setState({
              records: {
                [datasetId]: res.data,
              },
            });
          }
          // setPage((prev) => ({
          //   ...prev,
          //   page: 1,
          //   total: 1,
          // }));
        })
        .finally(() => {
          setLoading(false);
          abortControllerRef.current = undefined;
        });
    },
    [datasetId]
  );

  const handlePageChange = React.useCallback(
    (options: Partial<{ page: number; size: number }>) => setPage((prev) => ({ ...prev, ...options })),
    []
  );

  useLayoutEffect(() => {
    // 清理现有定时器（语言切换或其他参数变化时）
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }

    if (datasetId) {
      loadList({
        page: page.page,
        size: page.size,
        order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
        source_type: faqTypeValue ? faqTypeValue : undefined,
        start_time: rangeDate[0],
        end_time: rangeDate[1],
        language: i18n.language,
      });
    } else setLoading(false);
  }, [datasetId, loadList, page.page, page.size, faqTypeValue, rangeDate, reverseSortDirection, sortBy, i18n.language]);

  // 语言变化时立即清理所有旧状态，确保使用新语言重新开始
  React.useEffect(() => {
    // 立即清理现有的轮询定时器，避免使用旧语言继续轮询
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = undefined;
    }
    // 清理导入通知，让新语言的轮询可以重新显示通知
    hideNotification('importing-faq-list');
    // 如果正在加载中，立即设置为false，避免旧请求的状态影响新请求
    setLoading(false);
  }, [i18n.language]);

  // 组件卸载时清理资源
  React.useEffect(() => {
    return () => {
      // 清理导入通知
      hideNotification('importing-faq-list');
      // 清理定时器
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  return {
    loading,
    data,
    page,
    setPage: handlePageChange,
    loadList,
    searchList,
    faqTypeValue,
    setFaqTypeValue,
    handleDateChange,
    sortBy,
    reverseSortDirection,
    setSorting,
  };
};
